import { IBim360Project } from "@mctypes/forge.types";
import { IFilterItem } from "@mctypes/global.types";
import moment from "moment";
import { IRootState } from "../../../store";

const isValueEmpty = (value: unknown): boolean =>
  value == null || value === "" || (Array.isArray(value) && value.length === 0);

export const formatDate = (dateStr: string) => {
  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }
  return moment(dateStr).format("YYYY-MM-DD - HH:mm");
};

export const formatHtmlInputDateString = (isoString: string) => {
  if (isoString == null || isoString === "") {
    return "";
  }

  return moment(isoString).format("YYYY-MM-DD");
};

export const formatDateToNorwegianDate = (isoString: string) => {
  if (isoString == null || isoString === "") {
    return "";
  }

  return moment(isoString).format("DD.MM.YYYY");
};

export const parseNorwegianDate = (value: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;

  // Strict format check: dd.MM.yyyy
  const norwegianDateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
  if (!norwegianDateRegex.test(value)) return null;

  const [dayStr, monthStr, yearStr] = value.split(".");
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  const date = new Date(year, month - 1, day);

  // Validate that the constructed date matches input (catches invalid dates like 31.02.2026)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

export const formatShortDate = (dateStr: string | Date, hideOldDate: boolean = false): string => {
  if (dateStr instanceof Date) {
    dateStr = dateStr.toISOString();
  }

  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }

  if (hideOldDate && moment(dateStr).isBefore("2000-01-01")) {
    return "";
  }
  return moment(dateStr).format("DD.MM.YYYY");
};

export const formatDateWithoutYear = (dateStr: string) => {
  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }
  //format DD/MM
  return moment(dateStr).format("DD/MM");
};

export const formatInputDate = (dateStr: string) => {
  return moment(dateStr).format("YYYY.MM.DD");
};

export const isDateValidFrontend = (str: string): boolean => {
  if (str == null) {
    return false;
  }
  if (typeof str !== "string") {
    return false;
  }
  if (!str.match(/^\d/)) {
    return false;
  }
  if (str.length < 10) {
    return false;
  }
  let date = moment(str);
  return date.isValid();
};

export const getFolderUrn = (bim360Project: IBim360Project, nameOfFolder = "Project Files"): string => {
  const projectFolder = bim360Project.rootFolders.filter((folder) => folder.name === nameOfFolder)[0];
  return projectFolder.urn;
};

export const isFilterItemsValid = (filterItems: IFilterItem[]): boolean => {
  let isValid = true;
  if (isValueEmpty(filterItems)) {
    return false;
  }
  filterItems.forEach((item) => {
    if (isValueEmpty(item.label) || isValueEmpty(item.searchWord)) {
      isValid = false;
    }
  });
  return isValid;
};
/*
export const isAdmin = (state: IRootReducer) => {
  const { user } = state.globalReducer;
  if (user == null) {
    return false;
  }
  try {
    const member: IMember = find(state.adminReducer.project.members, (member) => member.user._id === user._id);
    return member && member.role === "ADMIN";
  } catch (err) {
    return false;
  }
};*/

export const isLoggedinUserMulticonsultUser = (state: IRootState) => {
  const { email } = state.globalReducer.user;
  return isEmailDomainMulticonsult(email);
};

export const getEmailDomain = (email: string) => {
  if (email == null) {
    return null;
  }
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase();
};

export const isEmailDomainMulticonsult = (email: string) => {
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase() === "@MULTICONSULT.NO";
};

export const isEmailDomainCowi = (email: string) => {
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase() === "@COWI.COM";
};

export const validateEmail = (email) => {
  var re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const emailShouldLoginWithOffice365 = (email: string) => {
  return isEmailDomainMulticonsult(email);
};

export const projectIsConnectedToBim360 = (state: IRootState) => {
  return state.adminReducer.project?.bim360.projectId != null;
};

export const projectIsConectedToArcGis = (state: IRootState) => {
  return state.adminReducer.project?.arcgis && state.adminReducer.project?.arcgis.groupId != null;
};

export const projectHasBotha2dAnd3dArcgisMap = (state: IRootState): boolean => {
  return (
    state.adminReducer.project?.arcgis?.default2dMap != null && state.adminReducer.project?.arcgis?.default3dMap != null
  );
};

// Converts a data URL to a File object
// Example usage: const file = dataURLToFile(dataUrl, "image.png");
export const dataURLToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};
